import { SensorDataType, SensorDisplayObject } from './SensorDisplayObject';
import * as settings from '@core_n/common/settings';
import { IObjectsPool, IMapMngr } from '@core_n/asp/interfaces';
import { SensorsCoreDrawer } from '@core_n/asp/SensorsCoreDrawer';
import { DisplayObject } from '@core_n/asp/DisplayObject';
import * as projConsts from '../common/projConsts';
import { NAT_NAI_COUNT, SensorObject } from './SensorObject';

export class SensorsDrawer extends SensorsCoreDrawer {
  protected type: string
  constructor(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    super(mapMngr, objectsPool, parentLayer);
    this.type = objectsPool.getObjectType();

    this.dataKeys.push(SensorDataType.Coverage);
    this.typeKeys.push(SensorDataType.Coverage);
    this.dataKeys.push(SensorDataType.NatNaiArea);
    this.typeKeys.push(SensorDataType.NatNaiArea);
    this.dataKeys.push(SensorDataType.Azimuth);
    this.typeKeys.push(SensorDataType.Azimuth);
    this.dataKeys.push(SensorDataType.AzimuthB);
    this.typeKeys.push(SensorDataType.AzimuthB);
    this.dataKeys.push(SensorDataType.AzimuthC);
    this.typeKeys.push(SensorDataType.AzimuthC);
  }

  protected addDrawTypes(images) {
    if (!this.is3D()) {
      this.addGeometryDrawType(SensorDataType.Coverage, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor });
      this.addFilledGeometryDrawType(SensorDataType.NatNaiArea, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha }, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha });
      this.addFilledGeometryDrawType(SensorDataType.Azimuth, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha }, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha });
      this.addFilledGeometryDrawType(SensorDataType.AzimuthB, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha }, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha });
      this.addFilledGeometryDrawType(SensorDataType.AzimuthC, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha }, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha });

    }

    const clusterDef = {
      layer: this.getLayer(),
      map: this.mapMngr.getMap(),
      showFullSelectedCluster: true,
      addObjectsAlways: true,
      clusterDistance: 20,
      maxClusterScale: 5000,
      textStyle: new NeWMI.Draw.Styles.FontStyle({ font: '14pt arial' }),
      backgroundStyle: new NeWMI.Draw.Styles.FontStyle({ font: '34pt Wingdings', charsRanges: [[108, 108]] }),
      backgroundValues: {
      },
    }
    clusterDef.backgroundValues[this.type] = { value: String.fromCharCode(108) }
    this.drawMgr.addType(NeWMI.Draw.DrawMgr.Enums.EKinds.Cluster, 'sensorsCluster', clusterDef);
    this.drawMgr.typesVisualProps.sensorsCluster.globalParams.selectedBackground.color = [1, 1, 1, 1];
    this.drawMgr.typesVisualProps.sensorsCluster.globalParams.background.color = [0.30, 0.35, 1.00, 0.70];
    super.addDrawTypes(images);
  }

  // Periodic refresh
  periodicUpdate(): boolean {
    if (!super.periodicUpdate())
      return false;

    if (this.drawMgr) {
      if (this.objectsLayerVisible) {
        for (let i = 0; i < NAT_NAI_COUNT; i++) {
          if (this.drawMgr.typesVisualProps[SensorDataType.NatNaiArea + i]) {
            this.drawMgr.typesVisualProps[SensorDataType.NatNaiArea + i].visible = settings.userSettings.filterOn[1];
          }
        }
        if (this.drawMgr.typesVisualProps[SensorDataType.Coverage]) {
          let coverageDisplayed = settings.userSettings.pointElementsOn[projConsts.POINT_DISPLAY_ELEMENT_COVERAGE];
          this.drawMgr.typesVisualProps[SensorDataType.Coverage].visible = true;
        }
        if (this.drawMgr.typesVisualProps[SensorDataType.Azimuth]) {
          this.drawMgr.typesVisualProps[SensorDataType.Azimuth].visible = true;
        }        
      }
    }
    return true;
  }

  // Override SensorsCoreDrawer
  createDisplayObject(obj: any): DisplayObject {
    return new SensorDisplayObject(obj, this);
  }

  // right click on sensor Nat/Nai area
  geometricObjectsAtLocation(point: NeWMI.Geometry.Point, theMap: any): SensorDisplayObject[] {
    let resSensorDisplay: SensorDisplayObject[] = new Array();
    let objects = this.getObjectsPool().getAllObjects();
    objects.forEach(obj => {
      let sensorObj = obj as SensorObject;
      if (sensorObj.naiNatArray) {
        let len = sensorObj.naiNatArray.length;
        for (let i = 0; i < len; i++)
          if (sensorObj.naiNatArray[i].naiNatShape) {
            let geometric = sensorObj.naiNatArray[i].naiNatShape as NeWMI.Geometry.Base.AGeometry;
            let hitResults = geometric.hitTest(theMap, point);
            if (hitResults) {
              if (hitResults.hitResult > 0) {
                let newObj = new SensorObject(this.getObjectsPool(), sensorObj.id, sensorObj.sensorLetter);
                newObj.sic = sensorObj.sic;
                newObj.sac = sensorObj.sac;
                newObj.naiNatAreaObject = sensorObj.naiNatArray[i];
                newObj.geometryMenuVisible = true;
                let displaySensor = new SensorDisplayObject(newObj, this);
                resSensorDisplay.push(displaySensor);
              }
            }
          }
      }
    });
    return resSensorDisplay;
  }
}

